import React, { useState, Suspense, useRef } from "react";
import { Tutorial, IconAtividades } from "@/assets";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

import LogoImage from "@/assets/Logo.png";
import DesktopSidebar from "./DesktopSidebar";
import ProgressBar from "./ProgressBar";

import { useEffect, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { PhaseFormFieldData, PhaseFormFieldType } from "../phaseForm/types";
import { AssetTypeField } from "./components/AssetTypeField";
import { ProcessRoleField } from "./components/ProcessRoleField";
import { TextField } from "./components/TextField";
import { StringField } from "./components/StringField";
import { MoneyField } from "./components/MoneyField";
import { DateField } from "./components/DateField";
import { SelectField } from "./components/SelectField";
import { InvoiceField } from "./components/InvoiceField";
import { UploadField } from "./components/UploadField";
import { StepStatus } from "./components/CustomStepIcon";
import { PageField } from "./components/PageField";
import { NumberField } from "./components/NumberField";
import { debounce } from "lodash";
import {
  SerializedUploadingFile,
  UploadingFile,
} from "./components/FileUploadManager";
import { isValidCPF, isValidEmail, isValidPhone } from "@/utils/maskUtils";
import CompleteFormCover from "./components/CompleteFormCover";
import { PersonFormModular } from "@/features/PersonFormModular";
import { FormStepLayout } from "./components/FormStepLayout";
import { SuccessPage } from "./components/SuccessPage";
import { ScheduleSuccessPage } from "./components/ScheduleSuccessPage";
import { FieldWrapper } from "./components/FieldWrapper";

interface FormWizardProps {
  formFields: PhaseFormFieldData[];
  onSubmit: (data: Array<{ field: PhaseFormFieldData; data: any }>) => void;
  processId: string;
  storageKey: string;
  id: string;
  title?: string;
  subtitle?: string;
  description?: string;
  descriptionTitle?: string;
  descriptionText?: string;
  phaseForm?: any;
  filledFormId?: string | null;
  isSubmitting?: boolean;
  products: any;
}

export const FormWizard: React.FC<FormWizardProps> = ({
  formFields,
  onSubmit,
  processId,
  storageKey,
  id,
  title = "",
  subtitle = "",
  description = "",
  descriptionTitle = "",
  descriptionText = "",
  phaseForm,
  filledFormId: propFilledFormId,
  isSubmitting,
  products,
}) => {
  const [showCover, setShowCover] = useState(true); // Estado para controlar se estamos na tela de boas-vindas
  const [showSteps, setShowSteps] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false); // Novo estado para controlar a página de sucesso
  const [completedSteps, setCompletedSteps] = useState<number[]>([]); // Array de índices dos passos completados
  const totalSteps = formFields.length;

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [fileData, setFileData] = useState<Record<string, any>>({});
  const [savedProcessId, setSavedProcessId] = useState<string>("");
  const [filledFormId, setFilledFormId] = useState<string | null>(
    propFilledFormId || null
  ); // Estado para armazenar o ID do formulário preenchido
  const [stepStatus, setStepStatus] = useState<StepStatus[]>(
    formFields.map(() => ({ visited: false, errors: true }))
  );
  const [errors, setErrors] = useState<boolean>(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null); // Para armazenar a data/hora do último salvamento
  const [dialogOpen, setDialogOpen] = useState(false);
  const [visibleSteps, setVisibleSteps] = useState(5);
  const [lsLog, setLsLog] = useState("");
  const isUpdatingRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true); // Estado para controlar se os dados estão sendo carregados
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Estado para controlar se é o carregamento inicial

  // Atualizar o estado de uploadingFiles para ser global ao form
  const [uploadingFilesState, setUploadingFilesState] = useState<
    Record<string, UploadingFile[]>
  >({});

  console.log("📹 FormWizard - Initial stepStatus:", stepStatus);
  console.log("📹 FormWizard - Initial formData:", formData);
  console.log("📹 FormWizard - Initial activeStep:", activeStep);
  console.log("📹 FormWizard - showCover:", showCover);
  console.log("📹 FormWizard - showSteps:", showSteps);
  console.log("📹 FormWizard - showSuccess:", showSuccess);

  // Função para serializar os dados antes de salvar no localStorage
  const serializeUploadingFiles = (files: Record<string, UploadingFile[]>) => {
    return Object.entries(files).reduce((acc, [key, value]) => {
      acc[key] = value.map((file) => ({
        fileName: file.fileName,
        fileSize: file.fileSize,
        progress: file.progress,
        status: file.status,
        path: file.path,
      }));
      return acc;
    }, {} as Record<string, SerializedUploadingFile[]>);
  };

  // Função para salvar dados no localStorage
  const saveToLocalStorage = (field: string, value: any) => {
    console.log(
      "📹 FormWizard - saveToLocalStorage called for field:",
      field,
      "isInitialLoad:",
      isInitialLoad
    );

    const savedData = localStorage.getItem(storageKey);
    const data = savedData
      ? JSON.parse(savedData)
      : { formData, fileData, stepStatus, activeStep, processId };

    // Serializa os dados de upload se necessário
    if (field === "uploadingFiles") {
      data[field] = serializeUploadingFiles(value);
    } else {
      data[field] = value;
    }

    localStorage.setItem(storageKey, JSON.stringify(data));
    setLsLog(data);
    const now = new Date();
    const formattedDate = now.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    setLastSaved(formattedDate);
    console.log("📹 FormWizard - Saved data:", data);
  };

  // Função para restaurar dados do localStorage
  const loadFromLocalStorage = () => {
    const savedData = localStorage.getItem(storageKey);
    console.log("📹 FormWizard - Loaded data:", storageKey, savedData);
    if (savedData) {
      const data = JSON.parse(savedData);
      console.log("📹 FormWizard - Parsed data:", data);

      if (data.formData) {
        console.log("📹 FormWizard - Setting formData:", data.formData);
        setFormData(data.formData);
      }
      if (data.fileData) {
        console.log("📹 FormWizard - Setting fileData:", data.fileData);
        setFileData(data.fileData);
      }
      if (data.processId) {
        console.log("📹 FormWizard - Setting processId:", data.processId);
        setSavedProcessId(data.processId);
        processId = data.processId;
      }
      if (data.uploadingFiles) {
        // Reconstrói os objetos File ao carregar do localStorage
        const reconstructedFiles = Object.entries(data.uploadingFiles).reduce(
          (acc, [key, value]) => {
            acc[key] = (value as SerializedUploadingFile[]).map((file) => ({
              ...file,
              file: new File([], file.fileName, {
                type: "application/octet-stream",
              }),
            }));
            return acc;
          },
          {} as Record<string, UploadingFile[]>
        );
        console.log(
          "📹 FormWizard - Setting uploadingFiles:",
          reconstructedFiles
        );
        setUploadingFilesState(reconstructedFiles);
      }
      if (data.stepStatus) {
        console.log("📹 FormWizard - Setting stepStatus:", data.stepStatus);
        setStepStatus(Object.values(data.stepStatus));
      }
      if (data.activeStep) {
        console.log("📹 FormWizard - Setting activeStep:", data.activeStep);
        setActiveStep(data.activeStep);

        // Se o activeStep é maior que 0, não devemos mostrar o cover
        if (data.activeStep > 0) {
          console.log(
            "📹 FormWizard - Setting showCover to false because activeStep > 0"
          );
          setShowCover(false);
        }
      }
      if (data.completedSteps) {
        console.log(
          "📹 FormWizard - Setting completedSteps:",
          data.completedSteps
        );
        setCompletedSteps(data.completedSteps);
      }
      setLastSaved(new Date().toLocaleString());
      setLsLog(savedData);
    } else {
      if (processId == "new") {
        processId = uuidv4();
        console.log("📹 FormWizard - New processId:", processId);
        setSavedProcessId(processId);
        // Só salva se não for o carregamento inicial
        if (!isInitialLoad) {
          saveToLocalStorage("processId", processId);
        }
      }
    }

    // Marca como carregado
    console.log("📹 FormWizard - Setting isLoading to false");
    setIsLoading(false);
    setIsInitialLoad(false);
  };

  // Efeito para carregar dados do localStorage ao montar o componente
  useEffect(() => {
    console.log("📹 FormWizard - Use Effect loadFromLocalStorage");
    console.log("📹 FormWizard - formFields length:", formFields.length);
    console.log("📹 FormWizard - current formData:", formData);

    loadFromLocalStorage();

    // Verificar se há um ID de formulário preenchido no localStorage
    if (id) {
      const formSpecificKey = `form_${id}_filledId`;
      const specificId = localStorage.getItem(formSpecificKey);

      if (specificId) {
        console.log(
          "FormWizard - FilledFormId encontrado na chave específica:",
          specificId
        );
        setFilledFormId(specificId);
      } else {
        // Verificar também na chave do storageKey
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          try {
            const data = JSON.parse(savedData);
            if (data.filledFormId) {
              console.log(
                "FormWizard - FilledFormId encontrado no storageKey:",
                data.filledFormId
              );
              setFilledFormId(data.filledFormId);
            }
          } catch (e) {
            console.error("Erro ao analisar dados do storageKey:", e);
          }
        }
      }
    }

    updateVisibleSteps(); // Atualiza inicialmente
    window.addEventListener("resize", updateVisibleSteps);
    return () => {
      window.removeEventListener("resize", updateVisibleSteps);
    };
  }, [formFields.length]); // Adicionar formFields.length como dependência

  // Função para limpar o formulário e o localStorage
  const clearForm = () => {
    setFormData({});
    localStorage.removeItem(storageKey); // Remove os dados do localStorage
    setLastSaved(null); // Reseta a data/hora do último salvamento
    setActiveStep(0);
    console.log("📹 FormWizard - Form cleared and localStorage reset");
  };

  // Atualiza o status do step atual quando os dados mudam
  useEffect(() => {
    // Previne atualizações recursivas
    if (isUpdatingRef.current) return;

    // Não executa durante o carregamento inicial
    if (isInitialLoad) return;

    isUpdatingRef.current = true;

    try {
      const currentField = formFields[activeStep];
      const isValid = validateStep(currentField);
      const currentStepStatus = stepStatus[activeStep];

      // Se for um campo do tipo PAGE, marca como visitado automaticamente
      if (currentField.type === PhaseFormFieldType.PAGE) {
        // Só atualiza se realmente houver mudança no status
        if (
          !currentStepStatus ||
          currentStepStatus.visited !== true ||
          currentStepStatus.errors !== false
        ) {
          // Cria uma cópia apenas se for necessário atualizar
          const newStepStatus = [...stepStatus];
          newStepStatus[activeStep] = {
            visited: true,
            errors: false,
          };

          // Compara stringificado para evitar atualizações desnecessárias
          if (JSON.stringify(stepStatus) !== JSON.stringify(newStepStatus)) {
            setStepStatus(newStepStatus);
            saveToLocalStorage("stepStatus", newStepStatus);
          }

          // Marca o campo como visitado no formData se ainda não estiver
          if (!formData[currentField._id]) {
            const newFormData = { ...formData, [currentField._id]: "visited" };
            setFormData(newFormData);
            saveToLocalStorage("formData", newFormData);
          }
        }
      } else {
        // Só atualiza se realmente houver mudança no status
        if (
          !currentStepStatus ||
          currentStepStatus.visited !== true ||
          currentStepStatus.errors !== !isValid
        ) {
          // Cria uma cópia apenas se for necessário atualizar
          const newStepStatus = [...stepStatus];
          newStepStatus[activeStep] = {
            visited: true,
            errors: !isValid,
          };

          // Compara stringificado para evitar atualizações desnecessárias
          if (JSON.stringify(stepStatus) !== JSON.stringify(newStepStatus)) {
            setStepStatus(newStepStatus);
            saveToLocalStorage("stepStatus", newStepStatus);
          }
        }
      }

      // Atualiza os passos completados
      const newCompletedSteps = formFields.reduce<number[]>(
        (completed, field, index) => {
          // Se é um campo do tipo PAGE e foi visitado, já conta como completo
          if (field.type === PhaseFormFieldType.PAGE) {
            return stepStatus[index]?.visited
              ? [...completed, index]
              : completed;
          }

          // Para outros campos, verifica se tem dados e se são válidos
          const hasData =
            formData[field._id] !== undefined &&
            formData[field._id] !== null &&
            formData[field._id] !== "";

          // Se o campo não é obrigatório e não tem dados, não inclui
          if (!field.required && !hasData) {
            return completed;
          }

          // Para campos não-PAGE, inclui se passar na validação E tiver dados
          if (validateStep(field) && hasData) {
            return [...completed, index];
          }

          return completed;
        },
        []
      );

      // Só atualiza completedSteps se houver mudança real
      const currentCompletedStepsStr = JSON.stringify(completedSteps.sort());
      const newCompletedStepsStr = JSON.stringify(
        [...newCompletedSteps].sort()
      );

      if (currentCompletedStepsStr !== newCompletedStepsStr) {
        setCompletedSteps(newCompletedSteps);
        saveToLocalStorage("completedSteps", newCompletedSteps);
      }
    } finally {
      // Sempre reseta a flag de atualização, mesmo em caso de erro
      isUpdatingRef.current = false;
    }
  }, [formData, fileData, activeStep, formFields, isInitialLoad]);

  const validateStep = (currentField: PhaseFormFieldData) => {
    if (!currentField) return true;

    // Se o campo não é obrigatório, sempre é válido
    if (!currentField.required) return true;

    // Se o campo aceita arquivos, verifica se tem valor ou arquivos
    if (currentField.acceptFiles) {
      const hasValue =
        formData[currentField._id] !== undefined &&
        formData[currentField._id] !== null &&
        formData[currentField._id] !== "";
      const hasFiles = fileData[currentField._id]?.length > 0;
      return hasValue || hasFiles;
    }

    switch (currentField.type) {
      case PhaseFormFieldType.PERSON:
        const value = formData[currentField._id];

        // Se não tem valor, não é válido
        if (!value) {
          return false;
        }

        // Se for multiline, precisa ter pelo menos um item válido
        if (currentField.multiline) {
          const values = Array.isArray(value) ? value : [value];
          if (values.length === 0) return false;

          // Cada item precisa ter os campos básicos preenchidos e válidos
          return values.every((person) => {
            return !!(
              person.name &&
              person.email &&
              person.cpf &&
              person.celphone &&
              isValidEmail(person.email) &&
              isValidCPF(person.cpf) &&
              isValidPhone(person.celphone)
            );
          });
        }

        // Para campo único, valida os campos básicos
        return !!(
          value.name &&
          value.email &&
          value.cpf &&
          value.celphone &&
          isValidEmail(value.email) &&
          isValidCPF(value.cpf) &&
          isValidPhone(value.celphone)
        );

      case PhaseFormFieldType.DATE:
      case PhaseFormFieldType.TEXT:
      case PhaseFormFieldType.SELECT:
      case PhaseFormFieldType.MONEY:
        const fieldValue = formData[currentField._id];
        return (
          fieldValue !== undefined && fieldValue !== null && fieldValue !== ""
        );
      default:
        return true;
    }
  };

  const handleSubmit = () => {
    // Se o formulário não está completo ou está em processo de submissão, não permite enviar
    if (!isFormComplete || isSubmitting) {
      console.log(
        "📹 FormWizard - Form is not complete or is already submitting"
      );
      return;
    }

    // Se o último campo é do tipo PAGE, consideramos o formulário completo
    const lastField = formFields[formFields.length - 1];
    const isLastFieldPage = lastField?.type === PhaseFormFieldType.PAGE;

    // Verificar se o último campo é válido
    const isLastFieldValid = validateStep(lastField);

    // Se o último campo não é válido e não é do tipo PAGE, não permite submeter
    if (!isLastFieldValid && !isLastFieldPage) {
      console.log("📹 FormWizard - Last field is not valid:", lastField);
      setErrors(true);
      return;
    }

    const formattedData = formFields.map((field) => ({
      field,
      data: field.acceptFiles
        ? {
            value: formData[field._id],
            files: fileData[field._id] || [],
          }
        : formData[field._id],
    }));

    console.log("Submitting form for process:", processId);
    onSubmit(formattedData);
    // Mostramos a página de sucesso local após o envio
    setShowSuccess(true);
  };

  const handleStepChange = (nextStep: number) => {
    // Garante que newStepStatus é um array de StepStatus
    const newStepStatus: StepStatus[] = Array.isArray(stepStatus)
      ? [...stepStatus]
      : Array(formFields.length).fill({ visited: false, errors: false });

    const currentField = formFields[activeStep];
    const isValid = validateStep(currentField);
    const visited = newStepStatus[activeStep].visited;

    // Se o campo atual é do tipo PAGE, sempre é válido quando visitado
    if (currentField.type === PhaseFormFieldType.PAGE) {
      newStepStatus[activeStep] = {
        visited: true,
        errors: false,
      };
    } else {
      newStepStatus[activeStep] = {
        visited: true,
        errors: !isValid && visited,
      };
    }

    setStepStatus(newStepStatus);
    saveToLocalStorage("stepStatus", newStepStatus);

    // Se é uma tentativa de submit (nextStep > último step)
    if (nextStep >= formFields.length) {
      // Se o campo atual é do tipo PAGE ou é válido, permite submeter
      if (currentField.type === PhaseFormFieldType.PAGE || isValid) {
        setErrors(false);
        handleSubmit();
      } else {
        setErrors(true);
      }
      return;
    }
    if (stepStatus?.[nextStep]) setErrors(stepStatus[nextStep].visited);
    // Atualiza o step ativo
    setActiveStep(nextStep);
    saveToLocalStorage("activeStep", nextStep);
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    let newFormData = { ...formData };
    newFormData[fieldId] = value;
    setFormData(newFormData);
    saveToLocalStorage("formData", newFormData);
  };

  const debouncedHandleFieldChange = useMemo(
    () => debounce(handleFieldChange, 0),
    [handleFieldChange]
  );

  const handleFileUploadChange = (
    formFieldId: string,
    uploadedFiles: string[],
    currentUploadingFiles?: UploadingFile[]
  ) => {
    // Atualiza fileData
    let newFileData = { ...fileData };
    newFileData[formFieldId] = uploadedFiles;
    setFileData(newFileData);

    // Atualiza uploadingFiles se fornecido
    if (currentUploadingFiles) {
      let newUploadingFilesState = { ...uploadingFilesState };
      newUploadingFilesState[formFieldId] = currentUploadingFiles;
      setUploadingFilesState(newUploadingFilesState);
      saveToLocalStorage("uploadingFiles", newUploadingFilesState);
    }

    saveToLocalStorage("fileData", newFileData);
  };

  const renderField = (field: PhaseFormFieldData) => {
    console.log(
      "📹 FormWizard - renderField called for field:",
      field._id,
      field.name
    );
    console.log(
      "📹 FormWizard - current formData for this field:",
      formData[field._id]
    );

    const commonProps = {
      value: formData[field._id] || "",
      onChange: (value: any) => handleFieldChange(field._id, value),
      label: field.name,
      description: field.description,
      required: field.required,
      videoUrl: field.videoUrl,
    };

    console.log(
      "📹 FormWizard - commonProps for field:",
      field._id,
      commonProps,
      products
    );

    const fieldComponent = (() => {
      switch (field.type) {
        case PhaseFormFieldType.ASSET:
          if (!field.assetTypeData)
            return <div>Tipo de ativo não configurado</div>;
          return (
            <AssetTypeField
              {...commonProps}
              assetType={field.assetTypeData}
              multiline={field.multiline}
            />
          );
        case PhaseFormFieldType.ROLE:
          if (!field.processRole)
            return <div>Papel do processo não configurado</div>;
          return (
            <ProcessRoleField
              {...commonProps}
              processRole={field.processRole}
              multiline={field.multiline}
            />
          );
        case PhaseFormFieldType.PERSON:
          return (
            <PersonFormModular
              {...commonProps}
              person={formData[field._id] || {}}
              multiline={field.multiline}
            />
          );
        case PhaseFormFieldType.TEXT:
          return <TextField {...commonProps} />;
        case PhaseFormFieldType.STRING:
          return (
            <StringField
              {...commonProps}
              name={field._id}
              onChange={(name: string, value: string) =>
                handleFieldChange(name, value)
              }
              mask={field.mask}
              isPassword={field.isPassword}
            />
          );
        case PhaseFormFieldType.MONEY:
          return <MoneyField {...commonProps} />;
        case PhaseFormFieldType.DATE:
          return <DateField {...commonProps} />;
        case PhaseFormFieldType.SELECT:
          return (
            <SelectField
              {...commonProps}
              options={field.options || []}
              multiline={field.multiline}
            />
          );
        case PhaseFormFieldType.NUMBER:
          return <NumberField {...commonProps} />;
        case PhaseFormFieldType.PAGE:
          return <PageField {...commonProps} />;
        case PhaseFormFieldType.INVOICE:
          return (
            <InvoiceField
              {...commonProps}
              processId={processId}
              phaseFormId={id}
              formFieldId={field._id}
              multiline={field.multiline}
            />
          );
        case PhaseFormFieldType.UPLOAD:
          return (
            <UploadField
              {...commonProps}
              processId={processId}
              phaseFormId={id}
              formFieldId={field._id}
              multiline={field.multiline}
            />
          );
        case PhaseFormFieldType.BINDING_QUESTION:
          return (
            <SelectField
              {...commonProps}
              options={products || []}
              multiline={true}
            />
          );
        default:
          return <div>Campo não suportado</div>;
      }
    })();

    return (
      <FormStepLayout
        key={field._id}
        activeStep={activeStep}
        totalSteps={totalSteps}
        onNext={handleNextStep}
        onPrevious={handlePreviousStep}
        onSubmit={handleSubmit}
        isValid={field.type === PhaseFormFieldType.PAGE || validateStep(field)}
        isFormComplete={isFormComplete && !isSubmitting}
      >
        <FieldWrapper
          field={field}
          onFileUpload={handleFileUploadChange}
          processId={processId}
          phaseFormId={id}
          uploadingFiles={uploadingFilesState[field._id]}
        >
          {fieldComponent}
        </FieldWrapper>
      </FormStepLayout>
    );
  };

  // Adicione esta função para verificar se há erros em qualquer step
  const hasAnyErrors = () => {
    console.log(stepStatus);
    console.log("-----------------------------------------------------");
    return stepStatus.some((status) => status.errors);
  };

  // Função para verificar se todos os campos obrigatórios foram preenchidos
  const isFormComplete = useMemo(() => {
    // Verificar se todos os campos obrigatórios foram validados
    return formFields.every((field, index) => {
      // Se o campo não é obrigatório, consideramos como válido
      if (field.required === false) {
        return true;
      }

      // Se o campo não foi visitado ainda, não está completo
      if (!stepStatus[index]?.visited) {
        return false;
      }

      // Se o campo foi visitado e não tem erros, está completo
      return !stepStatus[index]?.errors;
    });
  }, [formFields, stepStatus]);

  // const handleClearForm = () => {
  //   setDialogOpen(true);
  // };

  // const handleConfirmClear = () => {
  //   handleResetForm();
  //   setDialogOpen(false);
  // };

  const updateVisibleSteps = () => {
    const width = window.innerWidth;
    setVisibleSteps(Math.floor(width / 70) - 1); // Para telas grandes
  };

  // // Função para calcular o progresso geral do formulário
  // const calculateFormProgress = useMemo(() => {
  //   // Garante que stepStatus é um array de StepStatus
  //   const statusArray: StepStatus[] = Array.isArray(stepStatus)
  //     ? stepStatus
  //     : Array(formFields.length).fill({ visited: false, errors: true });

  //   const completedSteps: number = statusArray.reduce(
  //     (acc: number, step: StepStatus) => {
  //       if (step && step.visited && !step.errors) {
  //         return acc + 1;
  //       }
  //       return acc;
  //     },
  //     0
  //   );

  //   return {
  //     completedSteps,
  //     totalSteps: formFields.length,
  //   } as const; // Garante que o tipo do retorno é constante
  // }, [stepStatus, formFields.length]);

  const handleNextStep = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Função para avançar da tela de boas-vindas para o formulário
  const handleStartForm = () => {
    setShowCover(false);
  };

  // Limpa o formulário e retorna para a tela de boas-vindas
  const handleResetForm = () => {
    clearForm();
    setShowCover(true);
  };

  return (
    <main className="flex relative flex-col items-center bg-blend-normal h-full text-background">
      <div className="fixed inset-0 h-screen overflow-hidden -z-10 bg-gradient-to-br from-slate-900 to-slate-800">
        {/* Removido link estático da imagem de fundo */}
        <img
          src="https://cdn.builder.io/api/v1/image/assets/a957b64922e04ac2a23d8b53cd42fbd2/7b111fbabf0891ab0a671a7888ec71303d82a0de?placeholderIfAbsent=true"
          alt="Background"
          className="object-cover h-screen w-full"
        />
      </div>
      <article className="flex flex-col sm:flex-row gap-6 px-4 sm:px-6 py-2 sm:py-6 overflow-y-auto relative sm:justify-center h-full w-full">
        {/* Mobile Header */}
        {!showSuccess && (
          <header className="flex flex-col z-10 sm:hidden w-full">
            <div className="w-full flex items-center justify-center h-[70px] mb-2.5">
              <img src={LogoImage} alt="Logo" className="max-w-full max-h-[60px] h-auto object-contain" />
            </div>
            {!showCover && (
              <Dialog>
                <DialogContent className="aspect-video w-full bg-black/90 rounded-2xl border border-gray-800 text-grey-light">
                  {formFields[activeStep]?.videoUrl ? (
                    <iframe
                      src={formFields[activeStep].videoUrl}
                      style={{
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        border: 0,
                      }}
                      allowFullScreen
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share;"
                    ></iframe>
                  ) : (
                    <div className="flex items-center justify-center h-full text-grey-light">
                      <p>Nenhum vídeo explicativo disponível para este campo</p>
                    </div>
                  )}
                </DialogContent>
                <div className="w-full flex gap-4 justify-between items-center">
                  <DialogTrigger asChild className="z-10">
                    <Button
                      variant="ghost"
                      className="flex-1 bg-background/10 rounded-md gap-2"
                      disabled={!formFields[activeStep]?.videoUrl}
                    >
                      <Tutorial fill="var(--background)" />
                      Explicação
                    </Button>
                  </DialogTrigger>
                  <Button
                    variant="ghost"
                    className="flex-1 bg-background/10 rounded-md"
                    onClick={() => setShowSteps(true)}
                  >
                    <IconAtividades fill="var(--background)" />
                    Todas Etapas
                  </Button>
                </div>
              </Dialog>
            )}
          </header>
        )}

        {/* CONTENT */}
        {!showSteps && (
          <>
            <Suspense
              fallback={
                <div className="flex overflow-y-auto gap-4 sm:gap-10 z-0 grow flex-col p-5 sm:px-10 sm:pt-6 rounded-xl shadow-lg bg-background/10 w-full sm:max-w-[700px] sm:h-auto sm:flex-1 text-grey-light">
                  Carregando...
                </div>
              }
            >
              {isLoading ? (
                <div className="flex overflow-y-auto gap-4 sm:gap-10 z-0 grow flex-col p-5 sm:px-10 sm:pt-6 rounded-xl shadow-lg bg-background/10 w-full sm:max-w-[700px] sm:h-auto sm:flex-1 text-grey-light">
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                      <p>Carregando dados do formulário...</p>
                    </div>
                  </div>
                </div>
              ) : showSuccess ? (
                (() => {
                  // Verificar se o formulário tem agendamento habilitado
                  const hasAppointment = phaseForm?.hasAppointment === true;
                  const appointmentId = phaseForm?.appointmentId;

                  // Log para depuração fora do JSX
                  console.log("FormWizard - Verificando agendamento:", {
                    hasAppointment,
                    appointmentId,
                    phaseForm: phaseForm,
                  });

                  // Se o formulário tem agendamento habilitado e tem um appointmentId, mostrar página de agendamento
                  if (hasAppointment && appointmentId) {
                    console.log(
                      "FormWizard - Renderizando ScheduleSuccessPage com agendamento"
                    );
                    return (
                      <ScheduleSuccessPage
                        activeStep={activeStep}
                        totalSteps={totalSteps}
                        processId={filledFormId || processId}
                        appointmentId={appointmentId}
                        filledFormId={filledFormId}
                      />
                    );
                  } else {
                    // Caso contrário, mostrar página de sucesso simples
                    console.log(
                      "FormWizard - Renderizando SuccessPage simples (sem agendamento)"
                    );
                    return (
                      <SuccessPage
                        activeStep={activeStep}
                        totalSteps={totalSteps}
                      />
                    );
                  }
                })()
              ) : showCover ? (
                <CompleteFormCover
                  handleNextStep={handleStartForm}
                  handlePreviousStep={() => {}}
                  activeStep={1}
                  title={title}
                  subtitle={subtitle}
                  description={description}
                  descriptionTitle={descriptionTitle}
                  descriptionText={descriptionText}
                  totalSteps={totalSteps}
                />
              ) : (
                renderField(formFields[activeStep])
              )}
            </Suspense>
          </>
        )}

        {/* Desktop sidebar*/}
        {!showSuccess && !showCover && (
          <DesktopSidebar
            activeStep={activeStep}
            setShowSteps={setShowSteps}
            setActiveStep={setActiveStep}
            completedSteps={completedSteps}
            totalSteps={totalSteps}
            lastSaved={lastSaved}
            currentVideoUrl={formFields[activeStep]?.videoUrl}
          />
        )}

        {/* Mobile Progress */}
        {showSteps && !showSuccess && !showCover && (
          <ProgressBar
            activeStep={activeStep}
            setShowSteps={setShowSteps}
            setActiveStep={setActiveStep}
            completedSteps={completedSteps}
            totalSteps={totalSteps}
            lastSaved={lastSaved}
          />
        )}
      </article>
    </main>
  );
};
